// Root composition. AuthProvider initialises MSAL once, then react-router v7
// drives the route tree. AuthenticatedTemplate / UnauthenticatedTemplate from
// msal-react gate the whole tree on whether an active account exists; the
// useAuth hook elsewhere covers the /api/auth/me roundtrip independently.
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthProvider } from "./auth/AuthProvider";
import { AuthMeProvider } from "./auth/useAuth";
import { SignInScreen } from "./auth/SignInScreen";
import { AppShell } from "./components/layout/AppShell";
import { RequireToolAccess } from "./components/RequireToolAccess";
import { Dashboard } from "./pages/Dashboard";
import { Analytics } from "./pages/admin/Analytics";
import { Settings as AdminSettings } from "./pages/admin/Settings";
import { UserManagement } from "./pages/admin/UserManagement";
import { ProjectList } from "./pages/tools/pipe-takeoff/ProjectList";
import { NewProject } from "./pages/tools/pipe-takeoff/NewProject";
import { ProjectDetail } from "./pages/tools/pipe-takeoff/ProjectDetail";
import { ExtractionProgress } from "./pages/tools/pipe-takeoff/ExtractionProgress";
import { ReferenceLibrary } from "./pages/tools/pipe-takeoff/ReferenceLibrary";
import { TeamPlannerPage } from "./pages/tools/team-planner/TeamPlannerPage";
import { CutToLengthPage } from "./pages/tools/cut-to-length/CutToLengthPage";
import { ProductionSchedulePage } from "./pages/tools/production-schedule/ProductionSchedulePage";

function ProtectedRoutes() {
  return (
    <AuthMeProvider>
      <AppShell>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/tools/pipe-takeoff" element={<ProjectList />} />
          <Route path="/tools/pipe-takeoff/references" element={<ReferenceLibrary />} />
          <Route path="/tools/pipe-takeoff/new" element={<NewProject />} />
          <Route path="/tools/pipe-takeoff/:id/extract" element={<ExtractionProgress />} />
          <Route path="/tools/pipe-takeoff/:id" element={<ProjectDetail />} />
          <Route path="/tools/team-planner" element={<TeamPlannerPage />} />
          <Route
            path="/tools/cut-to-length"
            element={
              <RequireToolAccess toolId="cut-to-length">
                <CutToLengthPage />
              </RequireToolAccess>
            }
          />
          <Route path="/tools/production-schedule" element={<ProductionSchedulePage />} />
          <Route path="/tools/production-schedule/job/:jobId" element={<ProductionSchedulePage />} />
          <Route path="/tools/production-schedule/:view" element={<ProductionSchedulePage />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/analytics" element={<Analytics />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route
            path="*"
            element={
              <main className="bsp-content-wide">
                <h1 className="bsp-page-title">Not found</h1>
              </main>
            }
          />
        </Routes>
      </AppShell>
    </AuthMeProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthenticatedTemplate>
          <ProtectedRoutes />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <SignInScreen />
        </UnauthenticatedTemplate>
      </BrowserRouter>
    </AuthProvider>
  );
}
